import {
	Component,
	OnInit,
	AfterViewInit,
	ViewChild,
	ElementRef,
	HostListener, // Dodano za prisluškivanje Paste događaja
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import {
	TwoFactorService,
	ValidateTwoFactorResponse,
	ValidateRecoveryResponse,
} from "../../insa-services/insa-twofactor.service";
import { AuthService } from "../../insa-services";
import { appConfig } from "../../../insaweb.config";

type ValidateStep = "idle" | "verifying" | "recovery" | "error";

@Component({
	templateUrl: "twoFactorValidate.component.html",
})
export class TwoFactorValidateComponent implements OnInit, AfterViewInit {
	step: ValidateStep = "idle";
	errorMessage = "";
	useRecoveryCode = false;

	form: FormGroup;
	recoveryForm: FormGroup;

	@ViewChild("codeInput", { static: false })
	codeInput?: ElementRef<HTMLInputElement>;
	@ViewChild("recoveryInput", { static: false })
	recoveryInput?: ElementRef<HTMLInputElement>;
	@ViewChild("box0", { static: false }) box0?: ElementRef<HTMLInputElement>;
	@ViewChild("box1", { static: false }) box1?: ElementRef<HTMLInputElement>;
	@ViewChild("box2", { static: false }) box2?: ElementRef<HTMLInputElement>;
	@ViewChild("box3", { static: false }) box3?: ElementRef<HTMLInputElement>;
	@ViewChild("box4", { static: false }) box4?: ElementRef<HTMLInputElement>;
	@ViewChild("box5", { static: false }) box5?: ElementRef<HTMLInputElement>;

	// Pomoćni getter koji skuplja sva polja u niz radi lakše navigacije po indeksu
	get otpBoxes(): (ElementRef<HTMLInputElement> | undefined)[] {
		return [this.box0, this.box1, this.box2, this.box3, this.box4, this.box5];
	}

	constructor(
		private fb: FormBuilder,
		private twoFactorService: TwoFactorService,
		private authService: AuthService,
		public router: Router,
	) {
		this.form = this.fb.group({
			code: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
		});

		this.recoveryForm = this.fb.group({
			code: ["", [Validators.required]],
		});
	}

	ngOnInit(): void {
		if (!this.authService.hasPreTwoFactorState()) {
			this.router.navigate(["/login"]);
			return;
		}
	}

	ngAfterViewInit(): void {
		// Automatski fokusiramo PRVI kružić (box0) umjesto skrivenog inputa
		setTimeout(() => {
			if (this.box0 && this.box0.nativeElement) {
				this.box0.nativeElement.focus();
			}
		}, 0);
	}

	showRecoveryFocus(): void {
		setTimeout(() => this.recoveryInput?.nativeElement?.focus(), 0);
	}

	showAuthCodeFocus(): void {
		// Kada se korisnik vrati na ekran, ponovno fokusiramo prvi kružić
		setTimeout(() => {
			if (this.box0 && this.box0.nativeElement) {
				this.box0.nativeElement.focus();
			}
		}, 0);
	}

	// New: single place that decides what "2FA succeeded" means, based on
	// why the user got here. For a normal login this logs the user in as
	// before. For a password-reset flow, it deliberately does NOT create a
	// session - it only stashes a short-lived reset token and sends the
	// user to the reset-password screen.
	private handleVerifiedToken(token: string): void {
		if (this.authService.getTwoFactorPurpose() === "reset") {
			this.authService.setResetToken(token);
			this.authService.clearPreTwoFactorState();
			this.router.navigate(["/resetPassword"]);
			return;
		}

		this.authService.clearPreTwoFactorState();
		this.authService.performAuthenticaton(token);
		this.authService.emitLogedInStatusChange();
		this.router.navigate(["/"]);
	}

	submitCode(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const preAuthToken = this.authService.getPreTwoFactorToken();
		if (!preAuthToken) {
			this.errorMessage = "Your login session expired. Please sign in again.";
			this.step = "error";
			return;
		}

		this.step = "verifying";
		this.errorMessage = "";

		this.twoFactorService.validateTotp(this.form.value.code).subscribe({
			next: (res: ValidateTwoFactorResponse) => {
				if (!res || !res.token) {
					this.errorMessage =
						"Your login session expired. Please sign in again.";
					this.step = "error";
					return;
				}

				this.handleVerifiedToken(res.token);
			},
			error: (err) => {
				const culture =
					localStorage.getItem("selected-locale") ||
					(appConfig as any).defaultLocale ||
					"en-US";
				this.errorMessage =
					err?.error?.message ||
					appConfig.messages?.invalidVerificationCode?.[culture] ||
					"Invalid verification code. Please try again.";
				this.step = "idle";
				this.form.patchValue({ code: "" });

				this.otpBoxes.forEach((box) => {
					if (box && box.nativeElement) box.nativeElement.value = "";
				});
				this.box0?.nativeElement?.focus();
			},
		});
	}

	submitRecoveryCode(): void {
		if (this.recoveryForm.invalid) {
			this.recoveryForm.markAllAsTouched();
			return;
		}

		const preAuthToken = this.authService.getPreTwoFactorToken();
		if (!preAuthToken) {
			this.errorMessage = "Your login session expired. Please sign in again.";
			this.step = "error";
			return;
		}

		this.step = "verifying";
		this.errorMessage = "";

		this.twoFactorService
			.validateTotpRecovery(this.recoveryForm.value.code)
			.subscribe({
				next: (res: ValidateRecoveryResponse) => {
					if (!res || !res.token) {
						this.errorMessage =
							"Your login session expired. Please sign in again.";
						this.step = "error";
						return;
					}

					// Recovery-code path has an extra wrinkle even for normal
					// login: it can require the user to (re)do 2FA setup.
					if (res.requiresTwoFactorSetup) {
						if (this.authService.getTwoFactorPurpose() === "reset") {
							// A password reset must never be able to bootstrap
							// fresh 2FA setup - that would let anyone with a
							// recovery code re-enroll 2FA and take over the
							// account without ever proving the old password.
							this.authService.clearPreTwoFactorState();
							this.errorMessage =
								"Password reset is not available for this account yet. Please contact support.";
							this.step = "error";
							return;
						}

						this.authService.performAuthenticaton(res.token);
						this.router.navigate(["/twoFactorSetup"]);
						return;
					}

					this.handleVerifiedToken(res.token);
				},
				error: (err) => {
					this.errorMessage =
						err?.error?.message || "Invalid recovery code. Please try again.";
					this.step = this.useRecoveryCode ? "recovery" : "idle";
					this.recoveryForm.patchValue({ code: "" });
				},
			});
	}

	showRecovery(): void {
		this.useRecoveryCode = true;
		this.step = "recovery";
		this.errorMessage = "";
		this.showRecoveryFocus();
	}

	showAuthenticatorCode(): void {
		this.useRecoveryCode = false;
		this.step = "idle";
		this.errorMessage = "";
		this.showAuthCodeFocus();
	}

	onBoxInput(index: number, event: any): void {
		const input = event.target;
		input.value = input.value.replace(/\D/g, "");

		this.updateHiddenCode();

		if (input.value.length > 0 && index < 5) {
			const nextBox = this.otpBoxes[index + 1];
			if (nextBox && nextBox.nativeElement) {
				nextBox.nativeElement.focus();
				nextBox.nativeElement.select();
			}
		}
	}

	onBoxKeydown(index: number, event: KeyboardEvent): void {
		const input = event.target as HTMLInputElement;

		if (event.key === "Backspace" && !input.value && index > 0) {
			const prevBox = this.otpBoxes[index - 1];
			if (prevBox && prevBox.nativeElement) {
				prevBox.nativeElement.focus();
				prevBox.nativeElement.value = "";
			}
		}

		setTimeout(() => this.updateHiddenCode(), 0);
	}

	@HostListener("paste", ["$event"])
	onPaste(event: ClipboardEvent): void {
		const clipboardData = event.clipboardData;
		const pastedText = clipboardData?.getData("text") || "";

		if (/^\d{6}$/.test(pastedText)) {
			event.preventDefault();

			const digits = pastedText.split("");
			this.otpBoxes.forEach((box, i) => {
				if (box && box.nativeElement) {
					box.nativeElement.value = digits[i];
				}
			});

			this.updateHiddenCode();
			this.box5?.nativeElement?.focus();
		}
	}

	private updateHiddenCode(): void {
		let fullCode = "";
		this.otpBoxes.forEach((box) => {
			if (box && box.nativeElement) {
				fullCode += box.nativeElement.value;
			}
		});

		this.form.get("code")?.setValue(fullCode);
		this.form.get("code")?.markAsTouched();
	}
}


// WEBPACK FOOTER //
// ./app/insa-components/insa-login/twoFactorValidate.component.ts